import Link from '@/components/utilities/SmartLink';
import { Icon } from '@/components/ui/icon';
import { useAppContext } from '@/components/utilities/AppContext';

const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const BookIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MetaItem = ({ icon, children }) => (
  <span className="text-grey-600 flex items-center gap-2 text-sm">
    {icon}
    {children}
  </span>
);

const EbookHero = ({ eyebrow, headline, title, perex, publishLabel, chapterCount, readingTime, pdfUrl, pdfLabel }) => {
  const { locale } = useAppContext();
  const heroTitle = headline || title;

  return (
    <div className="from-grey-50 border-grey-200 relative overflow-hidden border-b bg-gradient-to-b to-white">
      <div
        aria-hidden="true"
        className="bg-brand-400/15 pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-green-400/10 blur-3xl"
      />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/ebook/ebook-hero-illustration.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 z-0 hidden h-[378px] w-auto select-none min-[1000px]:block"
      />

      <div className="relative z-10 mx-auto w-[90vw] pt-28 pb-16 xl:w-(--container-width) xl:pt-32">
        <div className="max-w-[720px]">
          {eyebrow && (
            <span className="text-brand-500 mb-5 inline-block text-sm font-bold tracking-[0.12em] uppercase">
              {eyebrow}
            </span>
          )}

          <h1 className="text-grey-950 mb-6 whitespace-pre-line">{heroTitle}</h1>

          {perex && <p className="text-grey-600 max-w-[640px] text-lg leading-[1.7]">{perex}</p>}

          {(publishLabel || chapterCount || readingTime) && (
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
              {publishLabel && <MetaItem icon={<CalendarIcon />}>{publishLabel}</MetaItem>}
              {chapterCount > 0 && (
                <MetaItem icon={<BookIcon />}>
                  {chapterCount} {locale !== 'pl' ? 'kapitol' : 'rozdziałów'}
                </MetaItem>
              )}
              {readingTime && <MetaItem icon={<ClockIcon />}>{readingTime}</MetaItem>}
            </div>
          )}

          {pdfUrl && (
            <div className="mt-8">
              <Link
                href={pdfUrl}
                target="_blank"
                rel="noopener"
                className="bg-brand-400 hover:bg-brand-500 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium text-white transition-colors"
              >
                <Icon type="documentDownload" width={16} height={16} />
                {pdfLabel || (locale !== 'pl' ? 'Stáhnout PDF' : 'Pobierz PDF')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EbookHero;
